// API utility for making authenticated requests
const API_BASE_URL = (() => {
  const explicit = process.env.REACT_APP_API_URL;
  if (explicit) {
    console.log('Using API URL from env:', explicit);
    return explicit;
  }

  if (typeof window !== 'undefined') {
    const host = window.location.hostname?.toLowerCase();
    const isLocal =
      host === 'localhost' ||
      host === '127.0.0.1' ||
      host === '::1';
    if (isLocal) return 'http://localhost:5000/api';
  }

  return 'https://app.pathmarkr.com/api';
})();

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = sessionStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
  };
};

// Handle API responses
const handleResponse = async (response) => {
  const contentType = response.headers.get('content-type') || '';
  const isJson = contentType.includes('application/json');
  if (!response.ok) {
    let data = {};
    try {
      data = isJson ? await response.json() : { message: await response.text() };
    } catch (_) {
      // ignore parse errors
    }
    const msg = data.message || data.error || response.statusText || 'Something went wrong';
    const err = new Error(msg);
    err.status = response.status;
    err.data = data;
    throw err;
  }
  return isJson ? response.json() : response.text();
};

// API methods
const api = {
  // GET request
  get: async (endpoint) => {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  // POST request
  post: async (endpoint, data) => {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  // PUT request
  put: async (endpoint, data) => {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  // DELETE request
  delete: async (endpoint) => {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  // Login method
  login: async (email, password) => {
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password }),
    });
    return handleResponse(response);
  },

  // Logout method
  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  },

  // Upload user profile picture
  uploadUserProfilePicture: async (userId, payload) => {
    const response = await fetch(`${API_BASE_URL}/users/${userId}/profile-picture`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    return handleResponse(response);
  },

  // Upload whitelabel agency logo
  uploadWhitelabelLogo: async (agencyId, payload) => {
    const response = await fetch(`${API_BASE_URL}/whitelabel-agencies/${agencyId}/logo`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    return handleResponse(response);
  },
};

// Export authHeader function for direct use
export const authHeader = () => {
  const token = sessionStorage.getItem('token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

export default api;
